import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Plus, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const CATEGORIES = ["General", "NICU Life", "Trach Care", "G-Tube", "Milestones", "Encouragement"];

export default function Forum() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "General", body: "" });
  const [filter, setFilter] = useState("All");

  const load = () => api.get("/topics").then((r) => setTopics(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Please add a title and message");
    try {
      await api.post("/topics", form);
      toast.success("Conversation started 💙");
      setOpen(false);
      setForm({ title: "", category: "General", body: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shown = filter === "All" ? topics : topics.filter((t) => t.category === filter);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Community Forum</h1>
          <p className="text-[#4A5568] mt-2">Conversations from families who understand.</p>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-topic-btn" className="inline-flex items-center gap-2 bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full px-6 py-3 font-heading font-semibold transition-colors">
                <Plus size={18} /> Start a topic
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading">Start a conversation</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="topic-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                <select data-testid="topic-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <textarea data-testid="topic-body-input" placeholder="Share what's on your heart…" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
              </div>
              <DialogFooter>
                <button data-testid="topic-submit-btn" onClick={create} className="bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">Post</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Link to="/auth" className="bg-[#6B8EAC] text-white rounded-full px-6 py-3 font-heading font-semibold">Sign in to post</Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} data-testid={`filter-${c.toLowerCase().replace(/ /g, "-")}`} onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-heading transition-colors ${filter === c ? "bg-[#6B8EAC] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568] hover:text-[#1A202C]"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid gap-4">
        {shown.length === 0 && (
          <div className="bg-white border border-[#6A0DAD] rounded-2xl p-12 text-center text-[#4A5568]">
            No conversations yet. Be the first to share.
          </div>
        )}
        {shown.map((t) => (
          <Link key={t._id} to={`/forum/${t._id}`} data-testid={`topic-card-${t._id}`}
            className="group bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex items-start justify-between gap-4">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-[#D4AF37]/12 text-[#B8972E] text-xs font-heading font-semibold mb-2">{t.category}</span>
              <h3 className="font-heading text-lg font-semibold text-[#1A202C] group-hover:text-[#6B8EAC] transition-colors">{t.title}</h3>
              <p className="text-[#4A5568] mt-1 line-clamp-2">{t.body}</p>
              <p className="text-xs text-[#4A5568] mt-3 flex items-center gap-3 font-heading">
                <span>by {t.author_name}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {new Date(t.last_activity).toLocaleDateString()}</span>
              </p>
            </div>
            <span className="flex items-center gap-1.5 text-[#6B8EAC] font-heading font-semibold shrink-0">
              <MessageCircle size={18} /> {t.reply_count}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
